/*
  Page dots and title arrows for the collection sections' sliders.

  Those sliders come in two flavours and this file handles both behind one
  small pager abstraction:

    - desktop carousel layout, driven by Flickity through <grid-slider>
    - mobile (and tablet) carousel, which is a native overflow-x scroller

  Flickity is configured with pageDots: false theme-wide and <grid-slider> is
  shared by many sections, so rather than flipping that flag we render our own
  controls and read the page count from whichever mechanism is live at the
  current breakpoint. Both controls hide themselves when there is only one
  page, which is what happens when a collection fits on screen.
*/
(function () {
  if (customElements.get('bb-slider-dots')) return;

  // A long collection would otherwise produce a dozen-plus dots. Past this
  // many they stop being countable, so they become evenly spaced positions
  // along the track rather than one dot per page.
  const MAX_DOTS = 8;

  function liveFlickity(track) {
    const flickity = window.theme && window.theme.Flickity;
    if (!flickity || !flickity.data) return null;
    const instance = flickity.data(track);
    return instance && instance.isActive ? instance : null;
  }

  /*
    Describes the track's paging at this instant. The native scroller's last
    page is nearly always a partial one, so its pages are spread across the
    distance the track can actually travel rather than placed every
    clientWidth - otherwise the final page sits past the end of the scroll
    range and can never be reached.
  */
  function pagerFor(track) {
    const flkty = liveFlickity(track);
    if (flkty) {
      const pages = flkty.slides.length;
      return {
        instance: flkty,
        pages: pages,
        index: flkty.selectedIndex,
        go: (i) => flkty.select(Math.max(0, Math.min(pages - 1, i))),
      };
    }

    const view = track.clientWidth;
    const max = Math.max(0, track.scrollWidth - view);
    if (!view || max < 2) {
      return {instance: null, pages: 1, index: 0, go: () => {}};
    }

    const pages = Math.max(2, Math.ceil(track.scrollWidth / view - 0.05));
    const step = max / (pages - 1);
    return {
      instance: null,
      pages: pages,
      index: Math.max(0, Math.min(pages - 1, Math.round(track.scrollLeft / step))),
      go: (i) => {
        const page = Math.max(0, Math.min(pages - 1, i));
        track.scrollTo({left: Math.round(page * step), behavior: 'smooth'});
      },
    };
  }

  /*
    Shared plumbing: resolve the track (which is rendered after the arrows, so
    it may not exist yet when they connect), then keep in step with scrolling,
    Flickity page changes and breakpoint switches.
  */
  class BBSliderControl extends HTMLElement {
    connectedCallback() {
      this.boundInstance = null;
      this.onChange = this.rafThrottle(() => this.refresh());
      this.onFlickityChange = () => this.refresh();

      window.addEventListener('resize', this.onChange);
      document.addEventListener('theme:resize:width', this.onChange);
      document.addEventListener('theme:slider:resize', this.onChange);

      this.tryResolve();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onChange);
      document.removeEventListener('theme:resize:width', this.onChange);
      document.removeEventListener('theme:slider:resize', this.onChange);
      if (this.track) this.track.removeEventListener('scroll', this.onChange);
    }

    tryResolve(attempt) {
      this.track = document.querySelector(this.dataset.track);
      if (!this.track) {
        // The arrows sit above the track in the markup, so on a first paint
        // it may not have parsed yet. <grid-slider> also needs a moment to
        // decide whether Flickity applies at this width.
        if ((attempt || 0) < 6) {
          setTimeout(() => this.tryResolve((attempt || 0) + 1), 150);
        }
        return;
      }
      this.track.addEventListener('scroll', this.onChange, {passive: true});
      this.refresh();
      setTimeout(() => this.refresh(), 400);
    }

    rafThrottle(fn) {
      let queued = false;
      return () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          fn();
        });
      };
    }

    refresh() {
      if (!this.track) return;
      const pager = pagerFor(this.track);

      // Flickity moves by transform rather than scrolling, so its own change
      // event is the only way to follow it. Rebind whenever the instance is
      // replaced, which happens when the slider is rebuilt on resize.
      if (pager.instance && this.boundInstance !== pager.instance) {
        this.boundInstance = pager.instance;
        pager.instance.on('change', this.onFlickityChange);
      }

      this.hidden = pager.pages < 2;
      if (!this.hidden) this.render(pager);
    }
  }

  customElements.define(
    'bb-slider-dots',
    class BBSliderDots extends BBSliderControl {
      render(pager) {
        const dots = Math.min(MAX_DOTS, pager.pages);

        if (this.children.length !== dots) {
          this.innerHTML = Array.from({length: dots}, (unused, i) =>
            `<button type="button" class="bb-dots__dot" aria-label="Go to page ${i + 1} of ${dots}"></button>`
          ).join('');
          Array.from(this.children).forEach((dot, i) => {
            // With fewer dots than pages a dot stands for a position along
            // the track rather than a single page.
            dot.addEventListener('click', () => {
              const target = dots > 1 ? Math.round((i * (pagerFor(this.track).pages - 1)) / (dots - 1)) : 0;
              pagerFor(this.track).go(target);
            });
          });
        }

        const active = pager.pages > 1 ? Math.round((pager.index * (dots - 1)) / (pager.pages - 1)) : 0;
        Array.from(this.children).forEach((dot, i) => {
          dot.classList.toggle('is-active', i === active);
          dot.setAttribute('aria-current', i === active ? 'true' : 'false');
        });
      }
    }
  );

  customElements.define(
    'bb-slider-arrow',
    class BBSliderArrow extends BBSliderControl {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.next = this.dataset.dir === 'next';
        if (this.button) {
          this.button.addEventListener('click', () => {
            const pager = pagerFor(this.track);
            pager.go(pager.index + (this.next ? 1 : -1));
          });
        }
        super.connectedCallback();
      }

      render(pager) {
        if (!this.button) return;
        const atEnd = this.next ? pager.index >= pager.pages - 1 : pager.index <= 0;
        this.button.disabled = atEnd;
      }
    }
  );
})();
